// InvoiceDetails.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { invoicesService } from "@/api/invoices";
import { downloadInvoicePDF, openInvoicePDF } from "@/utils/InvoicePDFGenerator";
import { useCredentials } from "@/hooks/useCredentials";
import { useTranslation } from "@/hooks/useTranslation";
import "./invoices.css";

const InvoiceDetails = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const companyId = user?.id;

    const [isPrinting, setIsPrinting] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // Fetch invoice data
    const {
        data: invoice,
        isLoading: invoiceLoading,
        error,
    } = useQuery({
        queryKey: ["invoice", id],
        queryFn: () => invoicesService.getInvoice(id),
        retry: false,
    });

    // Fetch company credentials (Émetteur/Issuer info)
    const {
        data: credentials,
        isLoading: credentialsLoading
    } = useCredentials();

    const isLoading = invoiceLoading || credentialsLoading;

    // Check if invoice belongs to current company
    useEffect(() => {
        if (invoice && companyId) {
            if ((invoice.company?.id || invoice.user?.id) !== companyId) {
                alert(t("invoices.business.accessDenied"));
                navigate("/factures");
            }
        }
    }, [invoice, companyId, navigate, t]);

    const getStatusColor = (status) => {
        switch (status) {
            case "PAID": return "bg-green-100 text-green-800";
            case "SUBMITTED": return "bg-yellow-100 text-yellow-800";
            case "REJECTED": return "bg-red-100 text-red-800";
            case "REFUNDED": return "bg-blue-100 text-blue-800";
            case "OVERDUE": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "PAID": return t("invoices.common.statusPaid");
            case "SUBMITTED": return t("invoices.business.statusSubmitted");
            case "REJECTED": return t("invoices.common.statusRejected");
            case "REFUNDED": return t("invoices.common.statusRefunded");
            case "OVERDUE": return t("invoices.business.overdueLabel");
            default: return status || t("invoices.common.statusPending");
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-TN', {
            style: 'currency',
            currency: 'TND'
        }).format(amount || 0);
    };

    const handleDownloadPDF = async () => {
        if (!invoice) return;

        try {
            setIsGeneratingPDF(true);
            await downloadInvoicePDF(invoice, `Facture-${invoice.id}.pdf`);
        } catch (error) {
            console.error("Failed to generate PDF:", error);
            alert(t("invoices.common.pdfGenerateError"));
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handlePreviewPDF = async () => {
        if (!invoice) return;

        try {
            setIsGeneratingPDF(true);
            await openInvoicePDF(invoice);
        } catch (error) {
            console.error("Failed to preview PDF:", error);
            alert(t("invoices.common.pdfOpenError"));
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handlePayInvoice = () => {
        navigate(`/factures/${id}/paiement`);
    };

    const isPayable = invoice?.status === 'SUBMITTED' || invoice?.status === 'OVERDUE';

    // Calculate totals for display
    const calculateTotals = () => {
        if (!invoice) return { subtotal: 0, totalDiscount: 0, afterDiscount: 0, total: 0 };

        const items = invoice.items || [];
        const subtotal = items.reduce((sum, item) => {
            const unitPrice = parseFloat(item.unitPrice) || 0;
            const quantity = parseInt(item.quantity) || 0;
            return sum + (unitPrice * quantity);
        }, 0);

        const totalDiscount = items.reduce((sum, item) => {
            return sum + (parseFloat(item.discountValue) || 0);
        }, 0);

        const afterDiscount = subtotal - totalDiscount;
        const taxAmount = parseFloat(invoice.tax) || 0;
        const total = afterDiscount + taxAmount;

        return { subtotal, totalDiscount, afterDiscount, taxAmount, total };
    };

    useEffect(() => {
        const handleAfterPrint = () => {
            setIsPrinting(false);
        };

        window.addEventListener('afterprint', handleAfterPrint);
        return () => {
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    if (!invoice) {
        return (
            <div className="p-6">
                <TaleDashbaordMainCard className="p-6">
                    <div className="text-center py-8">
                        <i className="fas fa-file-invoice text-4xl text-gray-300 mb-4"></i>
                        <h2 className="text-xl font-semibold text-gray-700 mb-2">
                            {t("invoices.common.notFound")}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {t("invoices.business.notFoundBody")}
                        </p>
                        <TaleButton onClick={() => navigate("/factures")}>
                            {t("invoices.common.backToInvoicesList")}
                        </TaleButton>
                    </div>
                </TaleDashbaordMainCard>
            </div>
        );
    }

    const { subtotal, totalDiscount, afterDiscount, taxAmount, total } = calculateTotals();

    // Helper function to split address lines for display
    const getAddressLines = (address) => {
        if (!address) return [];
        return address.split(',').map(line => line.trim());
    };

    return (
        <div className={`p-6 ${isPrinting ? "print-mode" : ""}`}>
            {/* Print styles */}
            <style>
                {`
          @media print {
            .no-print { display: none !important; }
            .print-mode * { color: black !important; }
            .print-container { 
              box-shadow: none !important; 
              border: 1px solid #ccc !important;
            }
            .print-table {
              border-collapse: collapse;
              width: 100%;
            }
            .print-table th, .print-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .print-table th {
              background-color: #f2f2f2;
            }
          }
        `}
            </style>

            {/* Invoice Content */}
            <TaleDashbaordMainCard className="print-container">
                <div className="no-print mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h1 className="text-3xl font-bold">{t("invoices.common.invoiceNumber", { id: invoice.id })}</h1>
                            <div className="flex items-center gap-4 mt-2">
                                <span className={`px-3 py-1 rounded-full ${getStatusColor(invoice.status)}`}>
                                    {getStatusText(invoice.status)}
                                </span>
                                <span className="text-gray-600">
                                    {t("invoices.common.createdOn", { date: formatDate(invoice.creationDate) })}
                                </span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <TaleButton onClick={() => navigate("/factures")}>
                                <i className="fas fa-arrow-left mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.common.back")}
                            </TaleButton>
                            {isPayable && (
                                <TaleButton
                                    onClick={handlePayInvoice}
                                    className="bg-green-600 hover:bg-green-700 border-green-600"
                                >
                                    <i className="fas fa-credit-card mr-2 rtl:mr-0 rtl:ml-2"></i>
                                    {t("invoices.business.payNow")}
                                </TaleButton>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                        <TaleButton
                            onClick={handleDownloadPDF}
                            loading={isGeneratingPDF}
                            disabled={isGeneratingPDF}
                        >
                            <i className="fas fa-download mr-2 rtl:mr-0 rtl:ml-2"></i>
                            {t("invoices.common.downloadPDF")}
                        </TaleButton>

                        <TaleButton
                            variant="outlined"
                            onClick={handlePreviewPDF}
                            loading={isGeneratingPDF}
                            disabled={isGeneratingPDF}
                        >
                            <i className="fas fa-eye mr-2 rtl:mr-0 rtl:ml-2"></i>
                            {t("invoices.common.previewPDF")}
                        </TaleButton>

                        {/* Overdue reminder */}
                        {invoice.status === "OVERDUE" && (
                            <TaleButton
                                variant="outlined"
                                className="!bg-red-50 !text-red-700"
                                onClick={() => alert(t("invoices.business.overdueReminderAlert"))}
                            >
                                <i className="fas fa-exclamation-triangle mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.business.overdueReminderBtn")}
                            </TaleButton>
                        )}

                        {/* Payment method info for paid invoices */}
                        {invoice.status === "PAID" && invoice.paymentMethod && (
                            <div className="ml-auto rtl:ml-0 rtl:mr-auto flex items-center bg-green-50 px-4 py-2 rounded-lg">
                                <i className="fas fa-check-circle text-green-600 mr-2 rtl:mr-0 rtl:ml-2"></i>
                                <span className="text-green-700">{t("invoices.business.paidVia", { method: invoice.paymentMethod })}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Header - Company credentials (Admin company) */}
                <div className="flex justify-between mb-8 pb-6 border-b">
                    <div>
                        <h2 className="text-2xl font-bold">{t("invoices.common.invoiceHeaderWord")}</h2>
                        <p className="text-gray-600 font-medium">{credentials?.companyName || t("invoices.business.admin")}</p>

                        {credentials?.address && getAddressLines(credentials.address).map((line, index) => (
                            <p key={index} className="text-gray-600">{line}</p>
                        ))}

                        {credentials?.phoneNumber && (
                            <p className="text-gray-600">{t("invoices.common.tel", { phone: credentials.phoneNumber })}</p>
                        )}

                        {credentials?.taxId && (
                            <p className="text-gray-600">{t("invoices.common.taxIdLine", { taxId: credentials.taxId })}</p>
                        )}
                    </div>

                    <div className="text-right rtl:text-left">
                        <div className="text-3xl font-bold mb-2">{t("invoices.common.invoiceNumber", { id: invoice.id })}</div>
                        <div className="text-gray-600">
                            {t("invoices.common.issueDateLine", { date: formatDate(invoice.creationDate) })}
                        </div>
                        {invoice.dueDate && (
                            <div className={`text-gray-600 ${invoice.status === 'OVERDUE' ? 'text-red-600 font-medium' : ''}`}>
                                {t("invoices.common.dueDateLine", { date: formatDate(invoice.dueDate) })}
                                {invoice.status === 'OVERDUE' && (
                                    <span className="ml-2 rtl:ml-0 rtl:mr-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded">
                                        {t("invoices.business.statusOverdueBadge")}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* From/To */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    {/* Émetteur - Admin company from credentials */}
                    <div>
                        <h3 className="font-bold text-lg mb-2">{t("invoices.common.issuer")}</h3>
                        <p className="font-bold">{credentials?.companyName || t("invoices.business.admin")}</p>

                        {credentials?.address && getAddressLines(credentials.address).map((line, index) => (
                            <p key={index}>{line}</p>
                        ))}

                        {credentials?.phoneNumber && (
                            <p>{t("invoices.common.tel", { phone: credentials.phoneNumber })}</p>
                        )}

                        {credentials?.taxId && (
                            <p>{t("invoices.common.taxIdLine", { taxId: credentials.taxId })}</p>
                        )}
                    </div>

                    {/* Client - Your company (current user) */}
                    <div>
                        <h3 className="font-bold text-lg mb-2">{t("invoices.common.client")}</h3>
                        <p className="font-bold">{user?.companyName || t("invoices.business.yourCompanyClient")}</p>
                        {user?.email && (
                            <p>{user.email}</p>
                        )}
                        {user?.address && (
                            <p>{user.address}</p>
                        )}
                        {user?.phoneNumber && (
                            <p>{t("invoices.common.tel", { phone: user.phoneNumber })}</p>
                        )}
                        {user?.taxId && (
                            <p>{t("invoices.common.taxIdLine", { taxId: user.taxId })}</p>
                        )}
                    </div>
                </div>

                {/* Items Table */}
                <div className="mb-8">
                    <table className="w-full border-collapse print-table">
                        <thead>
                        <tr className="bg-gray-50">
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.itemHeader")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.quantity")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.unitPriceHeader")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.discountPercentTh")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.discountAmountHeader")}</th>
                            <th className="border p-3 text-left rtl:text-right">{t("invoices.common.totalHeader")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(invoice.items || []).map((item, index) => {
                            const unitPrice = parseFloat(item.unitPrice) || 0;
                            const quantity = parseInt(item.quantity) || 0;
                            const discountPercentage = parseFloat(item.discountPercentage) || 0;
                            const discountValue = parseFloat(item.discountValue) || 0;
                            const itemTotal = (unitPrice * quantity) - discountValue;

                            return (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="border p-3">{item.name}</td>
                                    <td className="border p-3 text-center">{quantity}</td>
                                    <td className="border p-3">{formatCurrency(unitPrice)}</td>
                                    <td className="border p-3 text-center">{discountPercentage}%</td>
                                    <td className="border p-3">{formatCurrency(discountValue)}</td>
                                    <td className="border p-3 font-semibold">
                                        {formatCurrency(itemTotal)}
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>

                {/* Summary */}
                <div className="flex justify-end">
                    <div className="w-64">
                        <div className="flex justify-between mb-2">
                            <span>{t("invoices.common.subtotalLabel")}</span>
                            <span>{formatCurrency(subtotal)}</span>
                        </div>

                        {totalDiscount > 0 && (
                            <>
                                <div className="flex justify-between mb-2 text-green-600">
                                    <span>{t("invoices.common.totalDiscountLabel")}</span>
                                    <span>-{formatCurrency(totalDiscount)}</span>
                                </div>
                                <div className="flex justify-between mb-2 pt-2 border-t">
                                    <span>{t("invoices.common.amountAfterDiscount")}</span>
                                    <span className="font-semibold">{formatCurrency(afterDiscount)}</span>
                                </div>
                            </>
                        )}

                        {taxAmount > 0 && (
                            <div className="flex justify-between mb-2">
                                <span>{t("invoices.common.vatLabel")}</span>
                                <span>{formatCurrency(taxAmount)}</span>
                            </div>
                        )}

                        {invoice.discountAmount > 0 && (
                            <div className="flex justify-between mb-2 text-green-600">
                                <span>{t("invoices.common.discountAmountHeader")}:</span>
                                <span>-{formatCurrency(invoice.discountAmount)}</span>
                            </div>
                        )}

                        <div className="flex justify-between text-lg font-bold pt-2 border-t">
                            <span>{t("invoices.common.totalTTCLabel")}</span>
                            <span className="text-blue-600">{formatCurrency(total)}</span>
                        </div>
                    </div>
                </div>

                {/* Status and Payment Details */}
                <div className="mt-8 pt-6 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg mb-3">{t("invoices.common.statusLabel")}</h3>
                            <div className="flex items-center gap-3">
                                <span className={`px-3 py-2 rounded-full ${getStatusColor(invoice.status)}`}>
                                    {getStatusText(invoice.status)}
                                </span>
                                <div>
                                    <span className="text-sm text-gray-600">
                                        {invoice.status === "SUBMITTED" && t("invoices.business.awaitingYourPayment")}
                                        {invoice.status === "PAID" && t("invoices.common.paidOn", { date: formatDate(invoice.paymentDate) })}
                                        {invoice.status === "REJECTED" && t("invoices.common.invoiceRejected")}
                                        {invoice.status === "REFUNDED" && t("invoices.common.statusRefunded")}
                                        {invoice.status === "OVERDUE" && t("invoices.business.invoiceOverdueStatus")}
                                    </span>
                                    {invoice.status === "SUBMITTED" && invoice.dueDate && (
                                        <div className="text-sm text-gray-500 mt-1">
                                            {t("invoices.business.dueBefore", { date: formatDate(invoice.dueDate) })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="font-bold text-lg mb-3">{t("invoices.common.paymentDetails")}</h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                {invoice.status === "PAID" ? (
                                    <>
                                        <p><strong>{t("invoices.common.paymentMethodLabel")}</strong> {invoice.paymentMethod || t("invoices.business.notSpecified")}</p>
                                        {invoice.transactionId && (
                                            <p><strong>{t("invoices.business.reference")}</strong> {invoice.transactionId}</p>
                                        )}
                                        {invoice.paymentDate && (
                                            <p><strong>{t("invoices.business.paymentDate")}</strong> {formatDateTime(invoice.paymentDate)}</p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="font-medium mb-2">{t("invoices.business.toProceed")}</p>
                                        <ol className="list-decimal pl-5 rtl:pl-0 rtl:pr-5 space-y-1 text-sm">
                                            <li>{t("invoices.business.step1")}</li>
                                            <li>{t("invoices.business.step2")}</li>
                                            <li>{t("invoices.business.step3")}</li>
                                            <li>{t("invoices.business.step4")}</li>
                                        </ol>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer - Admin company info from credentials */}
                <div className="mt-10 pt-6 border-t text-center text-gray-600 text-sm">
                    <p className="font-medium">{credentials?.companyName || t("invoices.business.admin")}</p>

                    {credentials?.address && (
                        <p>{t("invoices.common.addressLine", { address: credentials.address })}</p>
                    )}

                    {credentials?.taxId && (
                        <p className="mt-2">{t("invoices.common.vatIntraLine", { taxId: credentials.taxId })}</p>
                    )}

                    {credentials?.phoneNumber && (
                        <p>{t("invoices.business.phoneLabel", { phone: credentials.phoneNumber })}</p>
                    )}
                </div>

                {/* Notes section */}
                {invoice.notes && (
                    <div className="mt-6 pt-6 border-t">
                        <h3 className="font-bold text-lg mb-3">{t("invoices.common.notesHeading")}</h3>
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <p className="text-gray-700">{invoice.notes}</p>
                        </div>
                    </div>
                )}

                {/* Company help section */}
                <div className="mt-8 pt-6 border-t">
                    <h3 className="font-bold text-lg mb-3">{t("invoices.business.needHelp")}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-medium text-blue-700 mb-2">
                                <i className="fas fa-question-circle mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.business.invoiceQuestions")}
                            </h4>
                            <p className="text-sm text-blue-600">
                                {t("invoices.business.contactForQuestions", { company: credentials?.companyName || t("invoices.business.admin") })}
                            </p>
                            {credentials?.phoneNumber && (
                                <p className="text-sm text-blue-600 mt-1">
                                    {t("invoices.business.phoneLabel", { phone: credentials.phoneNumber })}
                                </p>
                            )}
                        </div>
                        <div className="bg-green-50 p-4 rounded-lg">
                            <h4 className="font-medium text-green-700 mb-2">
                                <i className="fas fa-credit-card mr-2 rtl:mr-0 rtl:ml-2"></i>
                                {t("invoices.business.paymentIssue")}
                            </h4>
                            <p className="text-sm text-green-600">
                                {t("invoices.business.paymentIssueBody1")}
                            </p>
                            <p className="text-sm text-green-600 mt-1">
                                {t("invoices.business.paymentIssueBody2")}
                            </p>
                        </div>
                    </div>
                </div>
            </TaleDashbaordMainCard>
        </div>
    );
};

export default InvoiceDetails;
